'use client'

import { ReactNode, createContext, useContext, useRef, useEffect, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAtomValue } from 'jotai'
import { EvaluationProvider } from '~/store/evaluation-store/EvaluationProvider'
import { decodeSafeEvaluationSlug } from './utils'
import { useEvaluationQuery } from '~/app/evaluations/evaluationQuery'
import { useParams } from 'next/navigation'
import { EvaluationVersionClientQuery } from '~/gql/graphql'
import { QueryStatus } from '@tanstack/react-query'
import { useDashboardWorkspaceContext } from '~/app/(dashboard)/dashboardContext'
import { currentLocaleAtom } from '~/i18n/locale'

const EvaluationDocumentProviderContext = createContext<{
  status?: QueryStatus
  evaluation?: EvaluationVersionClientQuery['evaluation']
  refetch?: () => void
  loadStartTime?: number
  graphqlQueryTime?: number
}>({})

type EvaluationDocumentProviderProps = {
  children: ReactNode
}

export function useBaseEvaluation() {
  const contextValue = useContext(EvaluationDocumentProviderContext)
  const params = useParams()
  if (typeof params.slug === 'undefined') {
    throw new Error(
      'useBaseEvaluation must be used in the context of a valid evaluation slug/id'
    )
  }
  return contextValue
}

export const EvaluationDocumentProvider = ({
  children,
}: EvaluationDocumentProviderProps) => {
  const params = useParams()
  const locale = useAtomValue(currentLocaleAtom)
  // Get memberId from workspace context - don't load evaluation until we have memberId
  const { memberId: memberIdFromContext } = useDashboardWorkspaceContext()
  const hasMemberId = !!memberIdFromContext
  // Memoize memberId to prevent unnecessary provider resets when dashboard context updates
  const memberId = useMemo(() => memberIdFromContext, [memberIdFromContext])

  // Try to decode the slug, but if it fails (e.g., just an ID), use the slug directly
  let evaluationId: string | undefined
  try {
    const evaluationParams = decodeSafeEvaluationSlug(params.slug as string)
    evaluationId = evaluationParams?.id
  } catch {
    // If decode fails, assume the slug is just the evaluation_version_id
    evaluationId = decodeURIComponent(params.slug as string)
  }

  // Track load start time for performance metrics
  const loadStartTimeRef = useRef<number | null>(null)
  const graphqlQueryTimeRef = useRef<number | null>(null)
  const hasRecordedLoadTimeRef = useRef(false)
  const loadSessionIdRef = useRef<string | null>(null)

  // Set load start time when component mounts or evaluationId changes
  useEffect(() => {
    if (evaluationId && hasMemberId && !loadStartTimeRef.current) {
      loadStartTimeRef.current = performance.now()
      hasRecordedLoadTimeRef.current = false
      
      // Generate a session ID on the frontend to use as a key for matching start/end events
      const frontendSessionId = uuidv4()
      loadSessionIdRef.current = frontendSessionId
      
      // Record load start event — fire-and-forget via sendBeacon (zero rendering impact)
      const currentEvaluationId = evaluationId
      if (!currentEvaluationId) {
        return
      }

      try {
        const blob = new Blob(
          [JSON.stringify({ evaluationId: currentEvaluationId, sessionId: frontendSessionId })],
          { type: 'application/json' }
        )
        navigator.sendBeacon('/api/evaluation-load-start', blob)
      } catch {
        // sendBeacon failure is non-critical
      }
    }
  }, [evaluationId, hasMemberId])

  // Get the evaluation ID for the query
  // Use evaluationId if available, otherwise fall back to the slug
  const queryId = evaluationId || (params.slug as string)
  
  // Call the query immediately like the old code did - don't wait for hasMemberId
  // The query will run as soon as queryId is available
  const evaluationRequest = useEvaluationQuery(queryId)
  const status = evaluationRequest.status
  const evaluationData = evaluationRequest?.data || null
  const evaluation = evaluationData?.evaluation
  const refetch = evaluationRequest?.refetch

  // Track GraphQL query completion time
  useEffect(() => {
    if (status === 'success' && loadStartTimeRef.current && !graphqlQueryTimeRef.current) {
      const queryEndTime = performance.now()
      graphqlQueryTimeRef.current = (queryEndTime - loadStartTimeRef.current) / 1000 // Convert to seconds
    }
    
    // Record error if GraphQL query fails — fire-and-forget fetch
    if (status === 'error' && evaluationId) {
      fetch('/api/evaluation-load-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          evaluationId,
          sessionId: loadSessionIdRef.current,
          errorMessage: 'GraphQL query failed',
        }),
        keepalive: true,
      }).catch(() => {})
    }
  }, [status, evaluationId])

  // Use evaluation_version_id directly as roomName for websocket connection
  // This allows websocket to connect immediately even if evaluation query is still loading
  // Memoize roomName to prevent unnecessary provider resets when evaluation data loads
  // The roomName should be stable - use evaluationId (from slug) as primary source since it's available immediately
  // NOTE: This must be called before any early returns to satisfy React hooks rules
  const roomName = useMemo(() => {
    const slug = params.slug as string
    const stableId = evaluationId || (slug !== 'undefined' ? decodeURIComponent(slug) : undefined)
    const baseRoom = stableId || evaluation?.id || (slug !== 'undefined' ? decodeURIComponent(slug) : '')
    if (locale && locale !== 'en' && baseRoom) {
      return `${baseRoom}::${locale}`
    }
    return baseRoom
  }, [evaluationId, evaluation?.id, params.slug, locale])

  if (params.slug === 'undefined') {
    return <>{children}</>
  }
  return (
    <EvaluationDocumentProviderContext.Provider
      value={{ 
        status, 
        evaluation, 
        refetch,
        loadStartTime: loadStartTimeRef.current ?? undefined,
        graphqlQueryTime: graphqlQueryTimeRef.current ?? undefined,
      }}
    >
      <EvaluationProvider 
        key={`${roomName}-${memberId}`} // Stable key prevents remount when parent re-renders
        roomName={roomName}
        evaluationId={evaluationId || roomName}
        loadStartTime={loadStartTimeRef.current ?? undefined}
        loadStartTimeRef={loadStartTimeRef}
        graphqlQueryTime={graphqlQueryTimeRef.current ?? undefined}
        hasRecordedLoadTimeRef={hasRecordedLoadTimeRef}
        loadSessionId={loadSessionIdRef.current ?? undefined}
        loadSessionIdRef={loadSessionIdRef}
      >
        {children}
      </EvaluationProvider>
    </EvaluationDocumentProviderContext.Provider>
  )
}
